import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid, 
  LinearProgress,
  Paper,
  useTheme,
  styled,
  Avatar,
  Divider,
  Chip,
  Fade,
  Zoom,
  Tooltip,
  alpha
} from "@mui/material";
import { 
  Kitchen,
  MonetizationOn,
  TrendingUp,
  TrendingDown,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  CalendarToday,
  AttachMoney,
  Savings,
  Restaurant,
  Assessment,
  Timeline,
  ShowChart,
  Equalizer,
  Star,
  Warning,
  CheckCircle
} from "@mui/icons-material";
import { 
  BarChart as RechartsBarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import { format, parseISO, subDays } from 'date-fns';
import { motion, AnimatePresence } from "framer-motion";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Styled Components
const MetricCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(3),
  borderRadius: '20px',
  background: '#FFFFFF',
  boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: `0 20px 30px ${alpha(color || theme.palette.primary.main, 0.15)}`,
  },
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${color || theme.palette.primary.main}, ${alpha(color || theme.palette.primary.main, 0.3)})`,
  }
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: 450,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '24px',
  background: '#FFFFFF',
  boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
  },
  '& .title': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(1, 2),
    background: alpha(theme.palette.primary.main, 0.02),
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  }
}));

const StyledAvatar = styled(Avatar)(({ theme, color }) => ({
  width: 56,
  height: 56,
  background: `linear-gradient(135deg, ${color || theme.palette.primary.main}, ${alpha(color || theme.palette.primary.main, 0.7)})`,
  boxShadow: `0 8px 16px ${alpha(color || theme.palette.primary.main, 0.3)}`,
}));

const ProgressBar = styled(LinearProgress)(({ theme, color }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: alpha(theme.palette.divider, 0.2),
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    background: `linear-gradient(90deg, ${color || theme.palette.primary.main}, ${alpha(color || theme.palette.primary.main, 0.7)})`,
  }
}));

const ProductItem = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1),
  borderRadius: theme.spacing(2),
  background: '#FFFFFF',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateX(8px)',
    borderColor: alpha(theme.palette.primary.main, 0.3),
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
  }
}));

const CustomTooltip = ({ active, payload, label }) => {
  const theme = useTheme();
  
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ 
        p: 2, 
        borderRadius: 2,
        background: '#1A2B3C',
        color: 'white',
        minWidth: 180
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" sx={{ color: entry.color }}>
              {entry.name}:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {entry.value.toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'TND',
                minimumFractionDigits: 2
              })}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
};

export const DailyServingsAnalytics = ({ dailyServings }) => {
  const theme = useTheme();

  // Data calculations
  const totalRevenue = dailyServings.reduce((acc, serving) => {
    const revenue = serving.items.reduce((sum, item) => sum + (item.quantity * item.soldPrice), 0);
    return acc + revenue;
  }, 0);

  const totalCost = dailyServings.reduce((acc, serving) => {
    const cost = serving.items.reduce((sum, item) => sum + (item.quantity * item.costPrice), 0);
    return acc + cost;
  }, 0);

  const totalProfit = totalRevenue - totalCost;
  const profitMargin = ((totalProfit / totalRevenue) * 100) || 0;
  const daysCount = dailyServings.length;

  // Daily trend data
  const dailyTrendData = dailyServings.map(serving => {
    const revenue = serving.items.reduce((sum, item) => sum + (item.quantity * item.soldPrice), 0);
    const cost = serving.items.reduce((sum, item) => sum + (item.quantity * item.costPrice), 0);
    return {
      date: format(parseISO(serving.date), 'dd MMM'),
      revenue,
      cost,
      profit: revenue - cost,
      margin: ((revenue - cost) / revenue * 100) || 0
    };
  });

  // Product revenue distribution
  const productRevenueData = dailyServings.reduce((acc, serving) => {
    serving.items.forEach(item => {
      const existing = acc.find(p => p.name === item.productName);
      const revenue = item.quantity * item.soldPrice;
      const cost = item.quantity * item.costPrice;
      if (existing) {
        existing.revenue += revenue;
        existing.cost += cost;
        existing.profit += (revenue - cost);
        existing.quantity += item.quantity;
      } else {
        acc.push({ 
          name: item.productName, 
          revenue, 
          cost, 
          profit: revenue - cost,
          quantity: item.quantity,
          margin: ((revenue - cost) / revenue * 100) || 0
        });
      }
    });
    return acc;
  }, []).sort((a, b) => b.revenue - a.revenue);

  const colors = [
    '#4CAF50',
    '#2196F3',
    '#FF9800',
    '#9C27B0',
    '#f44336',
    '#00BCD4',
    '#795548',
    '#607D8B'
  ];

  const topProducts = productRevenueData.slice(0, 5);

  // Weekly comparison
  const today = new Date();
  const lastWeek = subDays(today, 7);
  
  const weeklyRevenue = dailyServings
    .filter(s => parseISO(s.date) >= lastWeek)
    .reduce((acc, s) => {
      const rev = s.items.reduce((sum, item) => sum + item.quantity * item.soldPrice, 0);
      return acc + rev;
    }, 0);

  const previousWeekRevenue = dailyServings
    .filter(s => parseISO(s.date) < lastWeek && parseISO(s.date) >= subDays(lastWeek, 7))
    .reduce((acc, s) => {
      const rev = s.items.reduce((sum, item) => sum + item.quantity * item.soldPrice, 0);
      return acc + rev;
    }, 0);

  const revenueGrowth = previousWeekRevenue ? 
    ((weeklyRevenue - previousWeekRevenue) / previousWeekRevenue * 100) : 0;

  // Performance metrics
  const bestDay = dailyTrendData.reduce((best, current) => 
    current.profit > (best?.profit || 0) ? current : best, null);
  
  const worstDay = dailyTrendData.reduce((worst, current) => 
    current.profit < (worst?.profit || 0) ? current : worst, null);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatCompactCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const Header = () => (
    <Paper 
      sx={{ 
        mb: 4,
        p: { xs: 3, md: 4 },
        borderRadius: '24px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        boxShadow: '0 20px 40px rgba(102,126,234,0.3)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 200,
        height: 200,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
        animation: 'float 6s infinite'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -40,
        left: -40,
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.05)',
        animation: 'float 8s infinite reverse'
      }} />

      <Grid container alignItems="center" spacing={3}>
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ 
                width: 64, 
                height: 64, 
                bgcolor: 'rgba(255,255,255,0.2)',
                animation: 'pulse 2s infinite'
              }}>
                <Kitchen sx={{ fontSize: 36 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  Analyse des Coûts Alimentaires
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    icon={<CalendarToday sx={{ fontSize: 14 }} />}
                    label={`${daysCount} jours analysés`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                  <Chip
                    icon={<Assessment sx={{ fontSize: 14 }} />}
                    label={`${productRevenueData.length} produits`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>
              </Box>
            </Box>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(255,255,255,0.1)', 
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Bénéfice Total
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {formatCompactCurrency(totalProfit)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    {totalProfit >= 0 ? 
                      <TrendingUp sx={{ fontSize: 16 }} /> : 
                      <TrendingDown sx={{ fontSize: 16 }} />}
                    <Typography variant="caption">
                      Marge: {profitMargin.toFixed(1)}%
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(255,255,255,0.1)', 
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Croissance Hebdo
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {revenueGrowth > 0 ? '+' : ''}{revenueGrowth.toFixed(1)}%
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    {revenueGrowth >= 0 ? 
                      <TrendingUp sx={{ fontSize: 16 }} /> : 
                      <TrendingDown sx={{ fontSize: 16 }} />}
                    <Typography variant="caption">
                      vs semaine dernière
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </motion.div>
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <Box sx={{ 
      p: { xs: 2, md: 4 }, 
      background: '#F5F7FA',
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Background decoration */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(102,126,234,0.03) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Header />

        {/* Metric Cards */}
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <MetricCard color="#4CAF50">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StyledAvatar color="#4CAF50">
                      <MonetizationOn sx={{ fontSize: 28 }} />
                    </StyledAvatar>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 0.5 }}>
                        Chiffre d'affaires
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                        {formatCompactCurrency(totalRevenue)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                        Total TTC
                      </Typography>
                    </Box>
                  </Box>
                </MetricCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <MetricCard color="#FF9800">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StyledAvatar color="#FF9800">
                      <Savings sx={{ fontSize: 28 }} />
                    </StyledAvatar>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 0.5 }}>
                        Coût Total
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                        {formatCompactCurrency(totalCost)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                        Matières premières
                      </Typography>
                    </Box>
                  </Box>
                </MetricCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <MetricCard color="#2196F3">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StyledAvatar color="#2196F3">
                      <Assessment sx={{ fontSize: 28 }} />
                    </StyledAvatar>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 0.5 }}>
                        Bénéfice Net
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: totalProfit >= 0 ? '#4CAF50' : '#f44336' }}>
                        {formatCompactCurrency(totalProfit)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                        Après déduction
                      </Typography>
                    </Box>
                  </Box>
                </MetricCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <MetricCard color="#9C27B0">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StyledAvatar color="#9C27B0">
                      <Timeline sx={{ fontSize: 28 }} />
                    </StyledAvatar>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 0.5 }}>
                        Marge Moyenne
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF9800' }}>
                        {profitMargin.toFixed(1)}%
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {profitMargin >= 30 ? 
                          <CheckCircle sx={{ fontSize: 14, color: '#4CAF50' }} /> : 
                          profitMargin >= 20 ? 
                            <Timeline sx={{ fontSize: 14, color: '#FF9800' }} /> : 
                            <Warning sx={{ fontSize: 14, color: '#f44336' }} />}
                        <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                          {profitMargin >= 30 ? 'Excellente' : profitMargin >= 20 ? 'Correcte' : 'À améliorer'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </MetricCard>
              </motion.div>
            </Grid>
          </Grid>

          {/* Performance Highlights */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Paper sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                  color: 'white'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Star sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Meilleure performance
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {bestDay?.date || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        Bénéfice: {bestDay?.profit ? formatCurrency(bestDay.profit) : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Paper sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                  color: 'white'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Warning sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Performance à améliorer
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {worstDay?.date || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        Bénéfice: {worstDay?.profit ? formatCurrency(worstDay.profit) : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>

        <Divider sx={{ my: 4, borderColor: alpha(theme.palette.divider, 0.1) }} />

        {/* Charts Section */}
        <Grid container spacing={3}>
          {/* Profit Trend Chart */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ChartContainer>
                <Box className="title">
                  <Timeline sx={{ color: '#4CAF50' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Évolution du Bénéfice
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height="90%">
                  <AreaChart data={dailyTrendData}>
                    <defs>
                      <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#4CAF50" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis 
                      dataKey="date"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                    />
                    <YAxis 
                      tickFormatter={(value) => 
                        value.toLocaleString('fr-FR', {
                          style: 'currency',
                          currency: 'TND',
                          minimumFractionDigits: 0
                        })}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="#4CAF50"
                      strokeWidth={3}
                      fill="url(#profitGradient)"
                      dot={{ r: 4, fill: '#4CAF50', strokeWidth: 2, stroke: '#FFFFFF' }}
                      activeDot={{ r: 8, fill: '#4CAF50', strokeWidth: 2, stroke: '#FFFFFF' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            </motion.div>
          </Grid>

          {/* Revenue vs Cost Chart */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <ChartContainer>
                <Box className="title">
                  <BarChartIcon sx={{ color: '#2196F3' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Revenus vs Coûts
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height="90%">
                  <ComposedChart data={dailyTrendData.slice(-7)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis 
                      dataKey="date"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                    />
                    <YAxis 
                      tickFormatter={(value) => 
                        value.toLocaleString('fr-FR', {
                          style: 'currency',
                          currency: 'TND',
                          minimumFractionDigits: 0
                        })}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="revenue" 
                      fill="#2196F3"
                      name="Revenus"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar 
                      dataKey="cost" 
                      fill="#FF9800"
                      name="Coûts"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="#4CAF50"
                      strokeWidth={2}
                      name="Bénéfice"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartContainer>
            </motion.div>
          </Grid>

          {/* Product Revenue Distribution */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ChartContainer>
                <Box className="title">
                  <PieChartIcon sx={{ color: '#9C27B0' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Distribution des Revenus par Produit
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <ResponsiveContainer width="60%" height="100%">
                    <PieChart>
                      <Pie
                        data={topProducts}
                        dataKey="revenue"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={50}
                        paddingAngle={2}
                        labelLine={false}
                      >
                        {topProducts.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={colors[index % colors.length]} 
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          background: '#1A2B3C',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          padding: 8
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ width: '40%', pl: 2 }}>
                    {topProducts.map((product, index) => (
                      <Box key={product.name} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {((product.revenue / totalRevenue) * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                        <ProgressBar 
                          variant="determinate" 
                          value={(product.revenue / totalRevenue) * 100}
                          color={colors[index]}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </ChartContainer>
            </motion.div>
          </Grid>

          {/* Product Performance Ranking */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <ChartContainer>
                <Box className="title">
                  <Equalizer sx={{ color: '#FF9800' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Performance des Produits
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
                  {productRevenueData.map((product, index) => (
                    <ProductItem
                      key={product.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        mr: 2, 
                        color: theme.palette.text.secondary,
                        minWidth: 30
                      }}>
                        #{index + 1}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600,
                            color: product.margin >= 30 ? '#4CAF50' : 
                                   product.margin >= 20 ? '#FF9800' : '#f44336'
                          }}>
                            {product.margin.toFixed(1)}% marge
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                            {formatCurrency(product.revenue)} • {product.quantity} vendus
                          </Typography>
                          <ProgressBar 
                            variant="determinate" 
                            value={(product.revenue / totalRevenue) * 100}
                            sx={{ width: '40%' }}
                            color={colors[index % colors.length]}
                          />
                        </Box>
                      </Box>
                    </ProductItem>
                  ))}
                </Box>
              </ChartContainer>
            </motion.div>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box sx={{ 
          mt: 4, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
            Dernière mise à jour: {format(new Date(), 'dd/MM/yyyy HH:mm')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip
              size="small"
              icon={<CheckCircle />}
              label={`${productRevenueData.filter(p => p.margin >= 30).length} produits excellents`}
              sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}
            />
            <Chip
              size="small"
              icon={<Warning />}
              label={`${productRevenueData.filter(p => p.margin < 20).length} produits à risque`}
              sx={{ bgcolor: alpha('#f44336', 0.1), color: '#f44336' }}
            />
          </Box>
        </Box>

        {/* CSS Animations */}
        <style jsx global>{`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-20px); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>
      </Box>
    </Box>
  );
};